import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useDeleteApiRipsByIdMutation,
  useGetApiRipsByIdQuery,
  useGetApiUsersMeQuery,
  usePostApiRipsByIdSubmitMutation,
} from '../../app/athenaApiGen';
import DynamicForm from '../../components/dynamic-form/DynamicForm';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import {
  createFormDataChangeHandler,
  parseRawFormData,
  toNumber,
} from '../../dashboard/components/formUtils';
import useSchemaForm from '../../hooks/useSchemaForm';
import { ripFormSchema } from './RIPFormSchema';

const DEBUG_MODE = true; // Toggle this for debug features

export default function RIPForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: meData } = useGetApiUsersMeQuery();
  const [formData, setFormData, isLoading, resetFormData] =
    useSchemaForm('RIPFormSchema');
  const { data: ripResp, isFetching: isFetchingRIP } = useGetApiRipsByIdQuery(
    { id: id || '' },
    { skip: !id },
  );
  const [deleteRip, { isLoading: deleting }] = useDeleteApiRipsByIdMutation();
  const [submitRip] = usePostApiRipsByIdSubmitMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isCloneMode = searchParams.get('mode') === 'clone';
  const [savedRipId, setSavedRipId] = React.useState(isCloneMode ? null : (id || null)); // Store ID after save

  // Always start fresh on no-id route navigations like /rip
  useEffect(() => {
    if (id) return;
    resetFormData();
    setSavedRipId(null);
    setDeleteDialogOpen(false);
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, [id, location.key, resetFormData]);

  const parsedRaw = useMemo(() => {
    const raw = ripResp?.rawFormData || ripResp?.RawFormData;
    return raw ? parseRawFormData(raw) : null;
  }, [ripResp]);

  // Initialize some default expense rows for new forms
  useEffect(() => {
    if (id) return;
    setFormData((prev) => {
      const items = prev?.expenses?.items;
      if (Array.isArray(items) && items.length > 0) return prev;
      const defaults = [
        {
          description: 'Travel Expenses',
          amount: '',
          category: 'Travel',
          date: null,
        },
        {
          description: 'Equipment',
          amount: '',
          category: 'Supplies',
          date: null,
        },
        {
          description: 'Training Materials',
          amount: '',
          category: 'Education',
          date: null,
        },
      ];
      return {
        ...prev,
        expenses: { ...(prev?.expenses || {}), items: defaults },
      };
    });
  }, [id, location.key, setFormData]);

  // Compute balance calculations reactively
  useEffect(() => {
    const clinValue = toNumber(formData?.clinx002Value);
    const cumulative = toNumber(formData?.cumulativeAmountBilled);
    const currentBal = clinValue - cumulative;
    const newBal = currentBal; // For RIP, new balance is same as current since it's reimbursement

    setFormData((prev) => {
      const next = { ...prev };
      const nextCurrentBal = String(currentBal.toFixed(2));
      const nextNewBal = String(newBal.toFixed(2));
      if (next.currentCLINX002Balance !== nextCurrentBal)
        next.currentCLINX002Balance = nextCurrentBal;
      if (next.newCLINX002Balance !== nextNewBal)
        next.newCLINX002Balance = nextNewBal;
      return next;
    });
  }, [formData?.clinx002Value, formData?.cumulativeAmountBilled, setFormData]);

  useEffect(() => {
    if (!id) return;
    if (!parsedRaw && ripResp) {
      const normalized = { ...ripResp };
      // Convert date fields to dayjs objects regardless of source
      if (normalized.ripDate) normalized.ripDate = dayjs(normalized.ripDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      normalized.workflowState = isCloneMode ? '' : (ripResp.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
      return;
    }
    if (parsedRaw) {
      const normalized = { ...parsedRaw };
      if (normalized.ripDate) normalized.ripDate = dayjs(normalized.ripDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      normalized.workflowState = isCloneMode ? '' : (ripResp?.workflowState ?? '');
      setFormData((prev) => ({ ...prev, ...normalized }));
    }
  }, [id, isCloneMode, parsedRaw, ripResp, setFormData]);

  if (isLoading || (id && isFetchingRIP && !parsedRaw)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleFormDataChange = createFormDataChangeHandler(setFormData);

  // Default to view mode when editing an existing record (id present),
  // unless explicitly set to edit via ?mode=edit.
  // Also force view mode if workflow has progressed past Pending.
  const editableWorkflow = !formData?.workflowState || formData.workflowState === 'Pending';
  const isViewMode = isCloneMode ? false : (id ? searchParams.get('mode') !== 'edit' || !editableWorkflow : false);

  // Handle cancel — act like the browser back button so callers from the
  // approval queue or elsewhere land back on the page they came from
  // instead of being forced to the RIP list.
  const handleCancel = () => {
    navigate(-1);
  };

  // Handle successful submission - navigate back to list
  const handleSubmitSuccess = (result) => {
    if (result.ok && result.payload) {
      // Store the returned ID for subsequent saves
      if (result.payload.ripId) {
        setSavedRipId(result.payload.ripId);
      }
    }
    if (result.ok && !result.skipNavigation) {
      setTimeout(() => navigate('/rip-list'), 2500);
    }
  };

  // Handle delete
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteRip({ id: id }).unwrap();
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'RIP deleted successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/rip-list'), 2500);
    } catch (error) {
      console.error('Failed to delete RIP', error);
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Failed to delete RIP. Please try again.',
        severity: 'error',
      });
    }
  };

  const canDelete = id && editableWorkflow && formData?.workflowState !== undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '100%' } }}>
      <DynamicForm
        schema={ripFormSchema}
        formData={formData}
        setFormData={handleFormDataChange}
        onSubmit={handleSubmitSuccess}
        showSubmit={isCloneMode || !savedRipId}
        isEditMode={!isCloneMode && Boolean(savedRipId)}
        recordId={isCloneMode ? null : savedRipId}
        readOnly={isViewMode}
        onCancel={handleCancel}
        onDelete={canDelete ? handleDeleteClick : undefined}
        submitMutation={submitRip}
        authorId={meData?.contact?.contactId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={deleteDialogOpen}
        title="Delete RIP"
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `
                        Are you sure you want to delete RIP <strong>${formData?.ripNumber}</strong>?
                        <br /><br />
                        This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
